import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';

interface FAQItem {
  question: string;
  answer: string;
}

interface SEOProps {
  title: string;
  description: string;
  canonical?: string;
  ogImage?: string;
  type?: 'website' | 'article' | 'service';
  jsonLd?: object;
  // Enhanced SEO/AEO/GEO props
  author?: string;
  publishedDate?: string;
  modifiedDate?: string;
  keywords?: string[];
  articleSection?: string;
  readingTime?: string;
  faqSchema?: FAQItem[];
  speakable?: string[];
  entities?: string[];
}

export function SEO({ 
  title, 
  description, 
  canonical, 
  ogImage = 'https://thecloudseo.com/og-image.png',
  type = 'website',
  jsonLd,
  author = 'The Cloud SEO',
  publishedDate,
  modifiedDate,
  keywords,
  articleSection,
  readingTime,
  faqSchema,
  speakable,
  entities,
}: SEOProps) {
  // Server-renderable head via react-helmet-async
  useEffect(() => {}, []);

  const scripts: Array<JSX.Element> = [];

  if (jsonLd) {
    scripts.push(
      <script key="page-jsonld" type="application/ld+json">
        {JSON.stringify(jsonLd)}
      </script>
    );
  }

  if (faqSchema && faqSchema.length > 0) {
    const faqJsonLd = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqSchema.map((faq) => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.answer,
        },
      })),
    };

    scripts.push(
      <script key="faq-jsonld" type="application/ld+json">
        {JSON.stringify(faqJsonLd)}
      </script>
    );
  }

  if (speakable && speakable.length > 0) {
    const speakableJsonLd = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": title,
      "speakable": {
        "@type": "SpeakableSpecification",
        "cssSelector": speakable,
      },
    };

    scripts.push(
      <script key="speakable-jsonld" type="application/ld+json">
        {JSON.stringify(speakableJsonLd)}
      </script>
    );
  }

  return (
    <Helmet>
      <title>{`${title} | The Cloud SEO`}</title>

      <meta name="description" content={description} />
      <meta name="author" content={author} />
      {keywords && keywords.length > 0 && (
        <meta name="keywords" content={keywords.join(', ')} />
      )}

      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:image" content={ogImage} />

      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {canonical && <link rel="canonical" href={canonical} />}

      {type === 'article' && publishedDate && (
        <meta property="article:published_time" content={publishedDate} />
      )}
      {type === 'article' && modifiedDate && (
        <meta property="article:modified_time" content={modifiedDate} />
      )}
      {type === 'article' && articleSection && (
        <meta property="article:section" content={articleSection} />
      )}

      {/* Inject JSON-LD scripts */}
      {scripts.map((s) => s)}
    </Helmet>
  );
}

// Helper function to generate Service schema
export function generateServiceSchema(service: {
  name: string;
  description: string;
  url: string;
  provider?: string;
  areaServed?: string[];
  priceRange?: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": service.name,
    "description": service.description,
    "url": service.url,
    "provider": {
      "@type": "Organization",
      "name": service.provider || "The Cloud SEO",
      "url": "https://thecloudseo.com",
    },
    "areaServed": service.areaServed || ["US", "IN", "AU", "AE"],
    "priceRange": service.priceRange || "$$$$",
  };
}

// Helper function to generate Article schema for insights
export function generateArticleSchema(article: {
  headline: string;
  description: string;
  url: string;
  author?: string;
  datePublished?: string;
  dateModified?: string;
  image?: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.headline,
    "description": article.description,
    "url": article.url,
    "author": {
      "@type": "Organization",
      "name": article.author || "The Cloud SEO",
    },
    "publisher": {
      "@type": "Organization",
      "name": "The Cloud SEO",
      "url": "https://thecloudseo.com",
      "logo": {
        "@type": "ImageObject",
        "url": "https://thecloudseo.com/logo.png",
      },
    },
    "datePublished": article.datePublished || new Date().toISOString(),
    "dateModified": article.dateModified || new Date().toISOString(),
    "image": article.image || "https://thecloudseo.com/og-image.png",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": article.url,
    },
  };
}
